import { useState, useEffect, useCallback } from "react";
import api from "services/auth";
import {
  Box, Card, CardContent, Chip, CircularProgress, Collapse,
  Dialog, DialogContent, DialogTitle, Divider, Grid, IconButton,
  InputAdornment, MenuItem, Paper, Stack, Table, TableBody,
  TableCell, TableContainer, TableHead, TablePagination, TableRow,
  TableSortLabel, TextField, Tooltip, Typography, Alert, Button,
} from "@mui/material";
import {
  Search, FilterList, Visibility, Close, AccessTime, Store,
  Person, TrendingUp, Receipt, AttachMoney, Schedule,
  ExpandMore, ExpandLess, Refresh,
} from "@mui/icons-material";

// ─── Config ───────────────────────────────────────────────────────────────────

const STATUT_CONFIG = {
  ouverte:   { label: "Ouverte",   color: "success" },
  fermee:    { label: "Fermée",    color: "default" },
  suspendue: { label: "Suspendue", color: "warning" },
};

const STATUT_OPTIONS = [
  { value: "",          label: "Tous les statuts" },
  { value: "ouverte",   label: "Ouverte"          },
  { value: "fermee",    label: "Fermée"            },
  { value: "suspendue", label: "Suspendue"         },
];

const DEFAULT_FILTERS = {
  search: "", statut: "", date_debut: "", date_fin: "", caissier_nom: "",
};

// ─── Hooks ────────────────────────────────────────────────────────────────────

function useSessionsCaisse(filters, page, rowsPerPage, order, orderBy) {
  const [data, setData]       = useState([]);
  const [total, setTotal]     = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState(null);

  const fetchSessions = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {
        page: page + 1,
        page_size: rowsPerPage,
        ordering: order === "desc" ? `-${orderBy}` : orderBy,
        ...Object.fromEntries(Object.entries(filters).filter(([, v]) => v !== "")),
      };
      const { data: res } = await api.get("depot/sessions-caisse/", { params });
      if (res?.results) { setData(res.results); setTotal(res.count); }
      else               { setData(res);         setTotal(res.length); }
    } catch (err) {
      setError(
        err?.response?.data?.detail ||
        err?.response?.data?.message ||
        "Erreur lors du chargement des sessions."
      );
    } finally {
      setLoading(false);
    }
  }, [filters, page, rowsPerPage, order, orderBy]);

  useEffect(() => { fetchSessions(); }, [fetchSessions]);
  return { data, total, loading, error, refetch: fetchSessions };
}

function useSessionDetail(id) {
  const [detail, setDetail]   = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState(null);

  useEffect(() => {
    if (!id) { setDetail(null); return; }
    setLoading(true); setError(null);
    api.get(`depot/sessions-caisse/${id}/`)
      .then(({ data }) => setDetail(data))
      .catch((err) =>
        setError(err?.response?.data?.detail || "Impossible de charger le détail.")
      )
      .finally(() => setLoading(false));
  }, [id]);

  return { detail, loading, error };
}

// ─── Utils ────────────────────────────────────────────────────────────────────

const fmt = (n) =>
  n == null
    ? "—"
    : new Intl.NumberFormat("fr-HT", {
        style: "currency", currency: "HTG", minimumFractionDigits: 2,
      }).format(n);

const fmtDate = (d) =>
  d
    ? new Intl.DateTimeFormat("fr-FR", {
        day: "2-digit", month: "short", year: "numeric",
        hour: "2-digit", minute: "2-digit",
      }).format(new Date(d))
    : "—";

// ✅ FIX : duree peut être un objet { heures, minutes, secondes, format }
const fmtDuree = (duree) => {
  if (!duree) return "En cours";
  if (typeof duree === "string") return duree;
  if (typeof duree === "object") return duree.format || `${duree.heures}h${duree.minutes}m`;
  return String(duree);
};

// ─── Petits composants ────────────────────────────────────────────────────────

function StatCard({ icon, label, value, color = "primary.main" }) {
  return (
    <Card variant="outlined" sx={{ flex: 1, minWidth: 140 }}>
      <CardContent sx={{ py: 1.5, px: 2, "&:last-child": { pb: 1.5 } }}>
        <Stack direction="row" alignItems="center" spacing={1} mb={0.5}>
          <Box sx={{ color, display: "flex" }}>{icon}</Box>
          <Typography variant="caption" color="text.secondary">{label}</Typography>
        </Stack>
        <Typography variant="h6" fontWeight={700} noWrap>{value}</Typography>
      </CardContent>
    </Card>
  );
}

function EcartChip({ ecart }) {
  if (ecart == null) return <Typography variant="body2" component="span">—</Typography>;
  const val   = parseFloat(ecart);
  const color = val === 0 ? "success" : val > 0 ? "info" : "error";
  const sign  = val > 0 ? "+" : "";
  return (
    <Chip
      label={`${sign}${fmt(val)}`}
      color={color} size="small" variant="outlined"
      sx={{ fontWeight: 600 }}
    />
  );
}

// ✅ FIX : component="span" pour éviter <p> dans <p>
function InfoRow({ label, value }) {
  return (
    <Stack
      direction="row" spacing={1} alignItems="center" py={0.5}
      sx={{ borderBottom: "1px solid", borderColor: "divider" }}
    >
      <Typography
        variant="body2"
        color="text.secondary"
        component="span"
        sx={{ minWidth: 120 }}
      >
        {label}
      </Typography>
      {typeof value === "string" || typeof value === "number"
        ? (
          <Typography variant="body2" fontWeight={500} component="span">
            {value || "—"}
          </Typography>
        )
        : value}
    </Stack>
  );
}

function NoteBox({ label, text, bgcolor = "grey.100" }) {
  return (
    <Box sx={{ bgcolor, borderRadius: 1.5, p: 1.5 }}>
      <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
        {label}
      </Typography>
      <Typography variant="body2">{text}</Typography>
    </Box>
  );
}

// ─── Modal Détail ─────────────────────────────────────────────────────────────

function DetailModal({ sessionId, open, onClose }) {
  const { detail, loading, error } = useSessionDetail(open ? sessionId : null);
  const resume = detail?.resume_paiements;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth
      PaperProps={{ sx: { borderRadius: 3 } }}>
      <DialogTitle>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Stack direction="row" spacing={1} alignItems="center">
            <Receipt color="primary" />
            <Typography variant="h6" fontWeight={700}>
              Détail session{detail ? ` — ${detail.reference}` : ""}
            </Typography>
          </Stack>
          <IconButton onClick={onClose} size="small"><Close /></IconButton>
        </Stack>
      </DialogTitle>
      <Divider />

      <DialogContent sx={{ pt: 2 }}>
        {loading && (
          <Box display="flex" justifyContent="center" py={6}>
            <CircularProgress />
          </Box>
        )}
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {detail && !loading && (
          <Stack spacing={3}>

            {/* ── Infos générales ── */}
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <InfoRow label="Référence"  value={detail.reference} />
                <InfoRow label="Succursale" value={detail.succursale_nom} />
                <InfoRow label="Caissier"   value={detail.caissier_nom} />
                <InfoRow label="Statut" value={
                  <Chip
                    label={STATUT_CONFIG[detail.statut]?.label || detail.statut}
                    color={STATUT_CONFIG[detail.statut]?.color || "default"}
                    size="small"
                  />
                } />
              </Grid>
              <Grid item xs={12} sm={6}>
                <InfoRow label="Ouverture"  value={fmtDate(detail.date_ouverture)} />
                <InfoRow label="Fermeture"  value={fmtDate(detail.date_fermeture)} />
                {/* ✅ FIX : fmtDuree() au lieu de detail.duree directement */}
                <InfoRow label="Durée"      value={fmtDuree(detail.duree)} />
                <InfoRow label="Fermée par" value={detail.ferme_par_nom || "—"} />
              </Grid>
            </Grid>

            {/* ── Montants ── */}
            <Box>
              <Typography variant="subtitle2" fontWeight={700} color="text.secondary" gutterBottom>
                MONTANTS
              </Typography>
              <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
                <StatCard icon={<AttachMoney fontSize="small" />} label="Ouverture" value={fmt(detail.montant_ouverture)} color="info.main" />
                <StatCard icon={<AttachMoney fontSize="small" />} label="Fermeture" value={fmt(detail.montant_fermeture)} color="success.main" />
                <StatCard icon={<AttachMoney fontSize="small" />} label="Attendu"   value={fmt(detail.montant_attendu)}   color="warning.main" />
              </Stack>
            </Box>

            {/* ── Résumé paiements ── */}
            {resume && (
              <Box>
                <Typography variant="subtitle2" fontWeight={700} color="text.secondary" gutterBottom>
                  RÉSUMÉ DES PAIEMENTS
                </Typography>
                <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap mb={2}>
                  <StatCard icon={<Receipt fontSize="small" />}     label="Complétées"      value={resume.nombre_ventes_completees} color="success.main" />
                  <StatCard icon={<Receipt fontSize="small" />}     label="Annulées"        value={resume.nombre_ventes_annulees}   color="error.main"   />
                  <StatCard icon={<AttachMoney fontSize="small" />} label="Espèces reçues"  value={fmt(resume.total_especes_recu)}  color="primary.main" />
                  <StatCard icon={<AttachMoney fontSize="small" />} label="Monnaie rendue"  value={fmt(resume.total_monnaie_rendue)} color="secondary.main" />
                </Stack>

                {resume.par_methode?.length > 0 && (
                  <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell><Typography variant="caption" fontWeight={700}>Méthode</Typography></TableCell>
                          <TableCell align="center"><Typography variant="caption" fontWeight={700}>Nb ventes</Typography></TableCell>
                          <TableCell align="right"><Typography variant="caption" fontWeight={700}>Total</Typography></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {resume.par_methode.map((row) => (
                          <TableRow key={row.methode_paiement} hover>
                            <TableCell>
                              <Chip label={row.methode_paiement} size="small" variant="outlined" />
                            </TableCell>
                            <TableCell align="center">{row.count}</TableCell>
                            <TableCell align="right">
                              <Typography fontWeight={600}>{fmt(row.total)}</Typography>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Box>
            )}

            {/* ── Notes ── */}
            {(detail.notes_ouverture || detail.notes_fermeture || detail.raison_ecart) && (
              <Box>
                <Typography variant="subtitle2" fontWeight={700} color="text.secondary" gutterBottom>
                  NOTES
                </Typography>
                <Stack spacing={1}>
                  {detail.notes_ouverture && <NoteBox label="À l'ouverture"    text={detail.notes_ouverture} />}
                  {detail.notes_fermeture && <NoteBox label="À la fermeture"   text={detail.notes_fermeture} />}
                  {detail.raison_ecart    && <NoteBox label="Raison de l'écart" text={detail.raison_ecart} bgcolor="warning.light" />}
                </Stack>
              </Box>
            )}
          </Stack>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ─── Filtres avancés ──────────────────────────────────────────────────────────

function FiltresAvances({ filters, onChange, onReset }) {
  return (
    <Card variant="outlined" sx={{ mb: 2, borderRadius: 2 }}>
      <CardContent sx={{ py: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              select fullWidth size="small" label="Statut"
              value={filters.statut} onChange={(e) => onChange("statut", e.target.value)}
            >
              {STATUT_OPTIONS.map((o) => (
                <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth size="small" type="date" label="Date début"
              InputLabelProps={{ shrink: true }}
              value={filters.date_debut} onChange={(e) => onChange("date_debut", e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth size="small" type="date" label="Date fin"
              InputLabelProps={{ shrink: true }}
              value={filters.date_fin} onChange={(e) => onChange("date_fin", e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth size="small" label="Caissier (nom)" placeholder="Rechercher..."
              value={filters.caissier_nom} onChange={(e) => onChange("caissier_nom", e.target.value)}
            />
          </Grid>
          <Grid item xs={12} display="flex" justifyContent="flex-end">
            <Button size="small" variant="outlined" color="inherit" onClick={onReset}>
              Réinitialiser les filtres
            </Button>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
}

// ─── Colonnes table ───────────────────────────────────────────────────────────

const COLUMNS = [
  { id: "reference",        label: "Référence"  },
  { id: "succursale_nom",   label: "Succursale", icon: <Store      fontSize="inherit" /> },
  { id: "caissier_nom",     label: "Caissier",   icon: <Person     fontSize="inherit" /> },
  { id: "date_ouverture",   label: "Ouverture",  icon: <Schedule   fontSize="inherit" /> },
  { id: "duree",            label: "Durée",      icon: <AccessTime fontSize="inherit" />, sortable: false },
  { id: "nombre_ventes",    label: "Ventes",     icon: <Receipt    fontSize="inherit" /> },
  { id: "chiffre_affaires", label: "CA",         icon: <TrendingUp fontSize="inherit" /> },
  { id: "ecart",            label: "Écart"  },
  { id: "statut",           label: "Statut" },
];

// ─── Page principale ──────────────────────────────────────────────────────────

export default function SessionsCaissePage() {
  const [filters, setFilters]         = useState(DEFAULT_FILTERS);
  const [page, setPage]               = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [order, setOrder]             = useState("desc");
  const [orderBy, setOrderBy]         = useState("date_ouverture");
  const [showFiltres, setShowFiltres] = useState(false);
  const [selectedId, setSelectedId]   = useState(null);
  const [detailOpen, setDetailOpen]   = useState(false);

  const { data, total, loading, error, refetch } =
    useSessionsCaisse(filters, page, rowsPerPage, order, orderBy);

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPage(0);
  };
  const handleResetFilters = () => { setFilters(DEFAULT_FILTERS); setPage(0); };
  const handleSort = (col) => {
    setOrder(orderBy === col && order === "asc" ? "desc" : "asc");
    setOrderBy(col);
    setPage(0);
  };
  const handleViewDetail = (id) => { setSelectedId(id); setDetailOpen(true); };

  const activeFiltersCount = Object.entries(filters)
    .filter(([k, v]) => v !== "" && k !== "search").length;

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>

      {/* ── En-tête ── */}
      <Stack direction="row" alignItems="center" justifyContent="space-between"
        mb={3} flexWrap="wrap" gap={1}>
        <Box>
          <Typography variant="h5" fontWeight={800}>Sessions de caisse</Typography>
          <Typography variant="body2" color="text.secondary">
            {total} session{total !== 1 ? "s" : ""} trouvée{total !== 1 ? "s" : ""}
          </Typography>
        </Box>
        <Tooltip title="Rafraîchir">
          <span>
            <IconButton onClick={refetch} disabled={loading}><Refresh /></IconButton>
          </span>
        </Tooltip>
      </Stack>

      {/* ── Barre recherche + toggle filtres ── */}
      <Stack direction="row" spacing={1} mb={2}>
        <TextField
          fullWidth size="small" placeholder="Rechercher par référence..."
          value={filters.search}
          onChange={(e) => handleFilterChange("search", e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search fontSize="small" color="action" />
              </InputAdornment>
            ),
          }}
          sx={{ maxWidth: 420 }}
        />
        <Button
          variant={showFiltres ? "contained" : "outlined"}
          startIcon={<FilterList />}
          endIcon={showFiltres ? <ExpandLess /> : <ExpandMore />}
          onClick={() => setShowFiltres((v) => !v)}
          sx={{ whiteSpace: "nowrap" }}
        >
          Filtres
          {activeFiltersCount > 0 && (
            <Chip label={activeFiltersCount} size="small" color="error"
              sx={{ ml: 0.5, height: 18, fontSize: 11 }} />
          )}
        </Button>
      </Stack>

      <Collapse in={showFiltres}>
        <FiltresAvances filters={filters} onChange={handleFilterChange} onReset={handleResetFilters} />
      </Collapse>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* ── Table ── */}
      <Paper variant="outlined" sx={{ borderRadius: 2, overflow: "hidden" }}>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                {COLUMNS.map((col) => (
                  <TableCell key={col.id}>
                    {col.sortable === false ? (
                      <Stack direction="row" alignItems="center" spacing={0.5}>
                        {col.icon}
                        <Typography variant="caption" fontWeight={700}>{col.label}</Typography>
                      </Stack>
                    ) : (
                      <TableSortLabel
                        active={orderBy === col.id}
                        direction={orderBy === col.id ? order : "asc"}
                        onClick={() => handleSort(col.id)}
                      >
                        <Stack direction="row" alignItems="center" spacing={0.5}>
                          {col.icon}
                          <Typography variant="caption" fontWeight={700}>{col.label}</Typography>
                        </Stack>
                      </TableSortLabel>
                    )}
                  </TableCell>
                ))}
                <TableCell align="center">
                  <Typography variant="caption" fontWeight={700}>Actions</Typography>
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={10} align="center" sx={{ py: 6 }}>
                    <CircularProgress size={32} />
                    <Typography variant="body2" color="text.secondary" mt={1}>
                      Chargement...
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : data.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={10} align="center" sx={{ py: 6 }}>
                    <Typography color="text.secondary">Aucune session trouvée</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                data.map((session) => (
                  <TableRow
                    key={session.id} hover sx={{ cursor: "pointer" }}
                    onClick={() => handleViewDetail(session.id)}
                  >
                    <TableCell>
                      <Typography variant="body2" fontWeight={600} fontFamily="monospace">
                        {session.reference}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{session.succursale_nom}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{session.caissier_nom}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" noWrap>{fmtDate(session.date_ouverture)}</Typography>
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" alignItems="center" spacing={0.5}>
                        <AccessTime fontSize="inherit" color="action" />
                        {/* ✅ FIX : fmtDuree() au lieu de session.duree directement */}
                        <Typography variant="body2">{fmtDuree(session.duree)}</Typography>
                      </Stack>
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="body2" fontWeight={600}>
                        {session.nombre_ventes ?? "—"}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>
                        {fmt(session.chiffre_affaires)}
                      </Typography>
                    </TableCell>
                    <TableCell><EcartChip ecart={session.ecart} /></TableCell>
                    <TableCell>
                      <Chip
                        label={STATUT_CONFIG[session.statut]?.label || session.statut}
                        color={STATUT_CONFIG[session.statut]?.color || "default"}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                      <Tooltip title="Voir le détail">
                        <IconButton size="small" color="primary"
                          onClick={() => handleViewDetail(session.id)}>
                          <Visibility fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[5, 10, 25, 50]}
          labelRowsPerPage="Par page :"
          labelDisplayedRows={({ from, to, count }) => `${from}–${to} sur ${count}`}
        />
      </Paper>

      {/* ── Modal détail ── */}
      <DetailModal
        sessionId={selectedId}
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
      />
    </Box>
  );
}
